import React, { useState } from 'react';
import { Upload, FileText, Copy, ArrowLeft, AlertTriangle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const VanGoBaseDownsReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations - Accept Excel files
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.xlsx', '.xls'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) && !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setError('Please upload an Excel file only (.xlsx or .xls)');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('excelFile', file);

      // Send file to server for processing
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/vangobasedowns`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return ' ❗ EXTREMELY VALUABLE';
    if (pct >= 70) return ' ⚠️ VALUABLE';
    return '';
  };

  const generateReportText = () => {
    if (!report) return '';

    let text = `🏈 VANGO BASE DOWNS ANALYSIS REPORT\n`;
    text += `Generated on ${new Date().toLocaleDateString()}\n\n`;

    if (report.logos?.clientLogo || report.logos?.companyLogo) {
      text += `📋 REPORT DETAILS\n`;
      if (report.logos?.clientLogo) text += `Client: [Logo included]\n`;
      if (report.logos?.companyLogo) text += `Analysis by: [Company logo included]\n`;
      text += `\n`;
    }

    text += `📊 DATA VERIFICATION\n`;
    text += `Using file: ${file?.name}\n`;
    text += `Total dataset rows: ${report.totalRows}\n`;
    text += `Base down plays: ${report.totalBaseDownPlays}\n`;
    text += `Verification complete: ${report.totalBaseDownPlays} total base down plays confirmed\n\n`;

    text += `📈 SUMMARY\n`;
    text += `Total Base Down Plays: ${report.totalBaseDownPlays}\n`;
    text += `Total Runs: ${report.totalRuns} (${report.runPercentage}%)\n`;
    text += `Total Passes: ${report.totalPasses} (${report.passPercentage}%)\n\n`;

    text += `👥 PERSONNEL USAGE ON BASE DOWNS\n`;
    text += `Total Personnel Groupings: ${report.personnelAnalysis?.length || 0}\n\n`;

    if (report.personnelAnalysis) {
      report.personnelAnalysis.forEach((personnel, index) => {
        text += `${index + 1}. ${personnel.personnel} Personnel: ${personnel.totalPlays} plays (${personnel.percentOfTotal}%)\n`;
        text += `   Total Runs: ${personnel.runs} (${personnel.runPercentage}%${getTendencyFlag(personnel.runPercentage)})\n`;
        text += `   Total Passes: ${personnel.passes} (${personnel.passPercentage}%${getTendencyFlag(personnel.passPercentage)})\n`;
        
        if (personnel.formations?.length > 0) {
          text += `   Formations Used:\n`;
          personnel.formations.forEach((formation, fIndex) => {
            text += `   ${fIndex + 1}. ${formation.formation}: ${formation.totalPlays} plays\n`;
            text += `    - Runs: ${formation.runs} (${formation.runPercentage}%${getTendencyFlag(formation.runPercentage)})\n`;
            text += `    - Passes: ${formation.passes} (${formation.passPercentage}%${getTendencyFlag(formation.passPercentage)})\n`;
          });
        }
        
        if (personnel.runConcepts && Object.keys(personnel.runConcepts).length > 0) {
          text += `   Run Concepts in ${personnel.personnel} Personnel (${personnel.runs} total run plays):\n`;
          Object.entries(personnel.runConcepts)
            .sort((a, b) => b[1] - a[1])
            .forEach(([concept, count]) => {
              const percentage = ((count / personnel.runs) * 100).toFixed(1);
              text += `   - ${concept}: ${count} plays (${percentage}%${getTendencyFlag(percentage)})\n`;
            });
        }
        text += `\n`;
      });
    }

    if (report.otherPersonnel?.length > 0) {
      text += `🔍 OTHER PERSONNEL GROUPINGS\n\n`;
      report.otherPersonnel.forEach((personnel) => {
        text += `${personnel.personnel} Personnel (${personnel.totalPlays} plays)\n`;
        if (personnel.runs === personnel.totalPlays) {
          text += `- 100% Run (${personnel.runs} of ${personnel.totalPlays} plays)\n\n`;
        } else if (personnel.passes === personnel.totalPlays) {
          text += `- 100% Pass (${personnel.passes} of ${personnel.totalPlays} plays)\n\n`;
        } else {
          text += `- Runs: ${personnel.runs} (${personnel.runPercentage}%), Passes: ${personnel.passes} (${personnel.passPercentage}%)\n\n`;
        }
      });
    }

    if (report.criticalTakeaways) {
      text += `🚨 CRITICAL DEFENSIVE TAKEAWAYS\n\n`;
      
      if (report.criticalTakeaways.extremely?.length > 0) {
        text += `Extremely Valuable Tendencies (≥80%) ❗\n\n`;
        report.criticalTakeaways.extremely.forEach(takeaway => {
          text += `- ${takeaway}\n`;
        });
        text += `\n`;
      }

      if (report.criticalTakeaways.valuable?.length > 0) {
        text += `Valuable Tendencies (70-79%) ⚠️\n\n`;
        report.criticalTakeaways.valuable.forEach(takeaway => {
          text += `- ${takeaway}\n`;
        });
      }
    }

    return text;
  };

  const copyReport = async () => {
    if (!report) return;

    const reportText = generateReportText();
    
    try {
      await navigator.clipboard.writeText(reportText);
      showSuccessMessage('Report copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const showSuccessMessage = (message) => {
    const originalError = error;
    setError(null);
    
    const alertDiv = document.createElement('div');
    alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    alertDiv.textContent = message;
    document.body.appendChild(alertDiv);
    
    setTimeout(() => {
      document.body.removeChild(alertDiv);
      setError(originalError);
    }, 3000);
  };

  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Header Section - Using Shared Component */}
        <ReportHeader 
          title="VANGO BASE DOWNS ANALYSIS"
          logos={report.logos}
          fileName={file?.name}
        />

        {/* Report Content */}
        <Box sx={{ p: 3 }}>

        {/* Data Verification Section */}
        <Box sx={{ mb: 4 }} data-section="vango-verification-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            DATA VERIFICATION
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f8f9fa'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.totalRows}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  TOTAL ROWS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#e3f2fd'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.totalBaseDownPlays}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  BASE DOWN PLAYS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f0f8ff'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.totalRuns}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  RUNS ({report.runPercentage}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#fff8f0'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.totalPasses}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  PASSES ({report.passPercentage}%)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Personnel Usage Summary */}
        <Box sx={{ mb: 4 }} data-section="vango-personnel-summary-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            PERSONNEL USAGE ON BASE DOWNS
          </Typography>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Total Personnel Groupings: {report.personnelAnalysis?.length || 0}
          </Typography>
          
          {report.personnelAnalysis && (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e9ecef' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', border: '1px solid #000' }}>PERSONNEL</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', border: '1px solid #000' }}>PLAYS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', border: '1px solid #000' }}>PERCENTAGE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.personnelAnalysis.map((personnel, index) => (
                    <TableRow key={personnel.personnel}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>{personnel.personnel}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '1rem', border: '1px solid #000' }}>{personnel.totalPlays}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '1rem', border: '1px solid #000' }}>{personnel.percentOfTotal}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Detailed Personnel Analysis */}
        {report.personnelAnalysis && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }} data-section="vango-personnel-header">
              DETAILED PERSONNEL ANALYSIS
            </Typography>
            
            {report.personnelAnalysis.map((personnel, index) => (
              <Box key={index} sx={{ mb: 4 }} data-section={`vango-personnel-${index}`}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2, bgcolor: '#e9ecef', p: 1, border: '1px solid #000' }}>
                  {personnel.personnel} PERSONNEL ({personnel.totalPlays} plays - {personnel.percentOfTotal}% of base downs)
                </Typography>
                
                {/* Personnel Summary */}
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {personnel.totalPlays}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        TOTAL PLAYS
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: parseFloat(personnel.runPercentage) >= 70 ? '#ffebee' : '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {personnel.runs}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        RUNS ({personnel.runPercentage}%)
                        {parseFloat(personnel.runPercentage) >= 80 && ' 🚨'}
                        {parseFloat(personnel.runPercentage) >= 70 && parseFloat(personnel.runPercentage) < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: parseFloat(personnel.passPercentage) >= 70 ? '#e3f2fd' : '#f8f9fa'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {personnel.passes}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        PASSES ({personnel.passPercentage}%)
                        {parseFloat(personnel.passPercentage) >= 80 && ' 🚨'}
                        {parseFloat(personnel.passPercentage) >= 70 && parseFloat(personnel.passPercentage) < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Formations Table */}
                {personnel.formations?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Formations Used:</Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
                      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#e9ecef' }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '35%' }}>FORMATION</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '12%' }}>PLAYS</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '12%' }}>RUNS</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '12%' }}>PASSES</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '14%' }}>RUN %</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000', width: '15%' }}>PASS %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {personnel.formations.map((formation, fIndex) => (
                            <TableRow key={fIndex}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem', border: '1px solid #000', wordWrap: 'break-word' }}>
                                {formation.formation}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem', border: '1px solid #000' }}>
                                {formation.totalPlays}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem', border: '1px solid #000' }}>
                                {formation.runs}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem', border: '1px solid #000' }}>
                                {formation.passes}
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontSize: '0.8rem', 
                                border: '1px solid #000',
                                bgcolor: parseFloat(formation.runPercentage) >= 80 ? '#ffcdd2' : 
                                         parseFloat(formation.runPercentage) >= 70 ? '#fff3e0' : 'inherit',
                                fontWeight: parseFloat(formation.runPercentage) >= 70 ? 'bold' : 'normal'
                              }}>
                                {formation.runPercentage}%
                                {parseFloat(formation.runPercentage) >= 80 && ' 🚨'}
                                {parseFloat(formation.runPercentage) >= 70 && parseFloat(formation.runPercentage) < 80 && ' ⚠️'}
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontSize: '0.8rem', 
                                border: '1px solid #000',
                                bgcolor: parseFloat(formation.passPercentage) >= 80 ? '#e1f5fe' : 
                                         parseFloat(formation.passPercentage) >= 70 ? '#f3e5f5' : 'inherit',
                                fontWeight: parseFloat(formation.passPercentage) >= 70 ? 'bold' : 'normal'
                              }}>
                                {formation.passPercentage}%
                                {parseFloat(formation.passPercentage) >= 80 && ' 🚨'}
                                {parseFloat(formation.passPercentage) >= 70 && parseFloat(formation.passPercentage) < 80 && ' ⚠️'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Run Concepts */}
                {personnel.runConcepts && Object.keys(personnel.runConcepts).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Run Concepts in {personnel.personnel} Personnel ({personnel.runs} total run plays):
                    </Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#e9ecef' }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>CONCEPT</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>COUNT</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>%</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(personnel.runConcepts)
                            .sort((a, b) => b[1] - a[1])
                            .map(([concept, count]) => {
                              const percentage = ((count / personnel.runs) * 100).toFixed(1);
                              return (
                                <TableRow key={concept}>
                                  <TableCell sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>{concept}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>{count}</TableCell>
                                  <TableCell align="center" sx={{ 
                                    fontSize: '0.9rem', 
                                    border: '1px solid #000',
                                    fontWeight: parseFloat(percentage) >= 70 ? 'bold' : 'normal',
                                    bgcolor: parseFloat(percentage) >= 80 ? '#ffcdd2' : 
                                             parseFloat(percentage) >= 70 ? '#fff3e0' : 'inherit'
                                  }}>
                                    {percentage}%
                                    {parseFloat(percentage) >= 80 && ' 🚨'}
                                    {parseFloat(percentage) >= 70 && parseFloat(percentage) < 80 && ' ⚠️'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Other Personnel Groupings */}
        {report.otherPersonnel?.length > 0 && (
          <Box sx={{ mb: 4 }} data-section="vango-other-personnel-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              OTHER PERSONNEL GROUPINGS
            </Typography>
            <Grid container spacing={2}>
              {report.otherPersonnel.map((personnel, index) => (
                <Grid item xs={12} md={6} lg={4} key={personnel.personnel}>
                  <Box sx={{ border: '1px solid #000', p: 2, bgcolor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {personnel.personnel} Personnel ({personnel.totalPlays} plays)
                    </Typography>
                    {personnel.runs === personnel.totalPlays ? (
                      <Typography variant="body2">
                        100% Run ({personnel.runs} of {personnel.totalPlays} plays)
                      </Typography>
                    ) : personnel.passes === personnel.totalPlays ? (
                      <Typography variant="body2">
                        100% Pass ({personnel.passes} of {personnel.totalPlays} plays)
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="body2">
                          Runs: {personnel.runs} ({personnel.runPercentage}%)
                        </Typography>
                        <Typography variant="body2">
                          Passes: {personnel.passes} ({personnel.passPercentage}%)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Critical Takeaways */}
        {report.criticalTakeaways && (report.criticalTakeaways.extremely?.length > 0 || report.criticalTakeaways.valuable?.length > 0) && (
          <Box sx={{ mb: 4 }} data-section="vango-takeaways-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              CRITICAL DEFENSIVE TAKEAWAYS
            </Typography>
            
            {report.criticalTakeaways.extremely?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, bgcolor: '#ffebee', p: 1, border: '1px solid #d32f2f' }}>
                  🚨 EXTREMELY VALUABLE TENDENCIES (≥80%)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTakeaways.extremely.map((takeaway, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffcdd2', 
                      border: '1px solid #d32f2f',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        • {takeaway}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {report.criticalTakeaways.valuable?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2, bgcolor: '#fff3e0', p: 1, border: '1px solid #f57c00' }}>
                  ⚠️ VALUABLE TENDENCIES (70-79%)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTakeaways.valuable.map((takeaway, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffe0b2', 
                      border: '1px solid #f57c00',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        • {takeaway}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        </Box> {/* End Report Content */}
      </Box>
    );
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.VANGO_BASE_DOWNS,
      'vango-base-downs-report.pdf',
      setError
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <TrendingUp size={32} />
        VanGo Base Downs Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your Excel file containing 1st and 2nd down data to generate a comprehensive offensive tendency report. 
        Analyzes personnel groupings, formations, run concepts, and critical defensive takeaways.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Upload size={20} />
            Upload Excel File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select an Excel file (.xlsx or .xls) containing base downs data with columns: Dn, R/P, PER, FINAL FORM, OFF PLAY. 
            The analysis will filter for base downs (Dn: 0, 1, 2, 22) and valid plays (R/P: R, P).
          </Typography>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-file-upload"
            disabled={loading}
          />
          <label htmlFor="excel-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose Excel File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate VanGo Base Downs Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your Excel data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your Excel data and generating the VanGo base downs report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={copyReport}
              startIcon={<Copy />}
            >
              Copy Report
            </Button>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="styled-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AlertTriangle size={20} />
            Column Mappings & Usage
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Required Excel Columns:</strong>
            <ul>
              <li><strong>Dn:</strong> Down number (0=first 1st down, 1=1st down, 2=2nd down, 22=2nd after incomplete)</li>
              <li><strong>R/P:</strong> Run/Pass indicator (R=run, P=pass, X=no snap excluded)</li>
              <li><strong>PER:</strong> Personnel groupings (11, 12, 10, etc.)</li>
              <li><strong>FINAL FORM:</strong> Formation information</li>
              <li><strong>OFF PLAY:</strong> Run concepts and play calls</li>
            </ul>
            <br />
            <strong>Analysis Process:</strong>
            <ol>
              <li>Upload Excel file with base downs data</li>
              <li>Tool filters for base downs (Dn: 0, 1, 2, 22) and valid plays (R/P: R, P)</li>
              <li>Analyzes personnel usage, formation tendencies, and run concepts</li>
              <li>Identifies critical takeaways (⚠️ = 70%+, ❗ = 80%+)</li>
              <li>Download complete report for game planning</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VanGoBaseDownsReportGenerator;